import { Entity } from 'vms-base';
import { LandscapeState } from 'vms-state';
import * as LocusLabsMap from 'vms-map';
import LliamUtilities from '@locuslabs/lliam-utilities';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import classNames from 'classnames';
import { Component } from 'react';
import { connect } from 'react-redux';
import * as VmsState from '../../backend/index-vms-state';
import { ConfirmationModal, PoiHistorySheet, Spinner, PoiName, PoiTabBar, PoiSave, DisplayEditor, ImageEditor, TagEditor, InfoEditor, JsonEditor, InternalEditor, PoiHeader } from '../../components';
import s from '../../components/poi-sheet/PoiSheet.module.scss';
import { ERRORCODES } from '../../backend/constants';
import * as validation from './validation';

const Routing = VmsState.Routing;
const { POI_STATUS_TYPE } = VmsState.constants;

const isClosed = poiId => R.startsWith('#', R.trim(poiId));
const convertToClosed = poiId => (isClosed(poiId) ? poiId : `#${R.trim(poiId)}`);
const convertToOpen = poiId => R.replace(/#/g, '', R.trim(poiId));

class PoiSheetContainer extends Component {
    static propTypes = {
        accountId: PropTypes.string,
        allTags: PropTypes.arrayOf(PropTypes.object),
        categories: PropTypes.arrayOf(PropTypes.object).isRequired,
        category: PropTypes.object,
        changedFields: PropTypes.arrayOf(PropTypes.string),
        dispatch: PropTypes.func.isRequired,
        errorMessage: PropTypes.string,
        feature: PropTypes.object,
        iconOptions: PropTypes.arrayOf(PropTypes.object),
        isDisabled: PropTypes.bool,
        isNameEmpty: PropTypes.bool.isRequired,
        isSaving: PropTypes.bool.isRequired,
        isV5: PropTypes.bool,
        location: PropTypes.object,
        modalOpen: PropTypes.bool,
        openLocation: PropTypes.bool,
        openOrClosed: PropTypes.string,
        poi: PropTypes.object,
        polygonId: PropTypes.string,
        rawFeatureId: PropTypes.string,
        isMovablePointPoi: PropTypes.bool,
        saveIsVisible: PropTypes.bool,
        selectedTabId: PropTypes.string.isRequired,
        showHistory: PropTypes.bool,
        showRemove: PropTypes.bool,
        terminals: PropTypes.arrayOf(PropTypes.string),
        terminalsToLevels: PropTypes.object,
        unsavedEdits: PropTypes.object,
        userData: PropTypes.object,
        v4Icon: PropTypes.string,
        movePoiMode: PropTypes.string,
        updatedPoiCoords: PropTypes.objectOf(PropTypes.number),
        lastSavedPoiCoords: PropTypes.objectOf(PropTypes.number),
    };

    static defaultProps = {
        accountId: '',
        allTags: [{}],
        category: '',
        changedFields: [],
        errorMessage: '',
        feature: {},
        iconOptions: [{}],
        isDisabled: true,
        isV5: false,
        location: {},
        modalOpen: false,
        openLocation: false,
        openOrClosed: 'open',
        poi: { id: '' },
        polygonId: '',
        rawFeatureId: '',
        isMovablePointPoi: false,
        saveIsVisible: false,
        showHistory: false,
        showRemove: false,
        terminals: [''],
        terminalsToLevels: {},
        unsavedEdits: {},
        userData: {},
        v4Icon: '',
        movePoiMode: '',
        updatedPoiCoords: null,
        lastSavedPoiCoords: null,
    };

    UNSAFE_componentWillReceiveProps(newProps) {
        const oldPip = { ...this.props.poi, ...this.props.unsavedEdits };
        const newPip = { ...newProps.poi, ...newProps.unsavedEdits };
        const { isV5 } = newProps;

        const shouldUpdateEntity =
            isV5 && oldPip.uuid === newPip.uuid && (oldPip.name !== newPip.name || oldPip.category !== newPip.category) && newPip.id && newPip.category && newPip.name && !newPip.id.includes('#');
        if (shouldUpdateEntity) {
            const { name, category } = newPip;
            const id = newPip.id.replace(/#/g, '');
            try {
                this.props.dispatch(LandscapeState.actionCreators.updateEntity({ entity: { id, name, category } }));
            } catch (e) {
                this.props.dispatch(Routing.Actions.goToErrorPage(ERRORCODES.UPDATE_ENTITY_ERROR));
            }
            this.props.dispatch(LocusLabsMap.actions.selectFeaturesForEntities([id]));
        }
    }

    getLocationInfo = () => {
        const { location } = this.props;

        const venueId = Routing.Selectors.locLocationVenueId(location);
        const view = Routing.Selectors.locLocationView(location);
        const query = view ? { view } : null;

        return { venueId, query };
    };

    isNewPoi = () => (this.props.poi ? R.test(/.*_NEW/, this.props.poi.uuid) : false);

    restoreFeature = () => {
        const { dispatch, feature } = this.props;
        dispatch(LandscapeState.actionCreators.addFeature(feature, feature.properties.levelId));
        dispatch(LocusLabsMap.actions.deselectAllFeatures());
        dispatch(VmsState.PoiStatusReducers.Actions.clearFeature());
    };

    onEdit = changes => {
        const { poi, dispatch } = this.props;
        const sideEffectUpdate = changes.terminal ? { floor: null } : {};
        dispatch(VmsState.PoiStatusReducers.Actions.editPoi(poi.uuid, { ...sideEffectUpdate, ...changes }));
    };

    onRevert = () => {
        const { poi, dispatch, movePoiMode, lastSavedPoiCoords } = this.props;
        dispatch(VmsState.PoiStatusReducers.Actions.revertPoi(poi.uuid));
        const { venueId, query } = this.getLocationInfo();

        if (movePoiMode) {
            dispatch(VmsState.ModeReducer.Actions.changeMovePoiMode(''));
            dispatch(
                LandscapeState.actionCreators.updateEntity({
                    entity: poi,
                    showBadge: true,
                    showName: false,
                    latLng: lastSavedPoiCoords,
                }),
            );
        }

        if (this.isNewPoi()) this.onClose();
        dispatch(Routing.Actions.goToVenue(venueId, query));
    };

    onSavePoi = async () => {
        const { poi, dispatch, unsavedEdits, openLocation, feature, movePoiMode, updatedPoiCoords } = this.props;
        if (validation.findFatalEntityEditValidationErrors(unsavedEdits)) return;

        const entities = R.mergeRight(poi, unsavedEdits);
        const unsavedEditsCheckFailedImages = R.omit(['imagesHasFailedImages', 'logoHasFailedImages'], unsavedEdits);
        if (movePoiMode) {
            dispatch(VmsState.ModeReducer.Actions.changeMovePoiMode('saving'));
        }
        const result = this.isNewPoi()
            ? await dispatch(VmsState.MultiThunks.createNewPoi(poi.venueId, unsavedEditsCheckFailedImages, openLocation, feature))
            : await dispatch(VmsState.MultiThunks.updateEntities(poi.venueId, [poi.uuid], unsavedEditsCheckFailedImages, false, updatedPoiCoords, entities));

        // remove this set state
        if (result) dispatch(VmsState.PoiStatusReducers.Actions.clearFeature());

        if (movePoiMode) {
            dispatch(VmsState.ModeReducer.Actions.changeMovePoiMode(''));
            dispatch(VmsState.PoiStatusReducers.Actions.showSuccessfulPoiMove(true));
            dispatch(VmsState.PoiStatusReducers.Actions.lastSavedPoiCoords(updatedPoiCoords));
            dispatch(VmsState.PoiStatusReducers.Actions.updatePoiCoords(null));
            setTimeout(() => {
                dispatch(VmsState.PoiStatusReducers.Actions.showSuccessfulPoiMove(false));
            }, 3000);
        }
    };

    onDuplicatePoi = () => {
        const { poi, dispatch } = this.props;
        const newPoi = { ...poi }; // clone the POI object to get field value
        delete newPoi.uuid; // (gets set in reducer)
        delete newPoi.numericId; // (derived data)

        newPoi.name = `Copy of ${poi.name}`;
        newPoi.id = R.startsWith('#', poi.id) ? '#' : ''; // is it closed or not

        R.forEachObjIndexed((value, key) => {
            // rem
            if (value === null || key === '$loki') {
                delete newPoi[key];
            }
            if ((key === 'tags' || key === 'displayTags') && value.length === 0) {
                delete newPoi[key];
            }
        }, newPoi);

        dispatch(VmsState.PoiStatusReducers.Actions.startNewPoi(poi.venueId, newPoi));
        dispatch(Routing.Actions.goToPoi(poi.venueId, `${poi.venueId}_NEW`));
    };

    onClose = () => {
        const { dispatch, location, unsavedEdits, feature } = this.props;

        const venueId = Routing.Selectors.locLocationVenueId(location);
        const view = Routing.Selectors.locLocationView(location);
        const query = view ? { view } : null;

        if (this.isNewPoi() && unsavedEdits.id) {
            const entityId = unsavedEdits.id.replace(/#/g, '');
            dispatch(LandscapeState.actionCreators.removeEntity(entityId, true));
            dispatch(LocusLabsMap.actions.deselectAllFeatures());
        }
        // restore a previous feature since openPolygon will have deleted the old feature
        if (!R.isEmpty(feature)) this.restoreFeature();

        dispatch(Routing.Actions.goToVenue(venueId, query));
    };

    onRemove = async () => {
        const { dispatch, poi, unsavedEdits, isV5 } = this.props;
        const entities = R.mergeRight(poi, unsavedEdits);
        const id = unsavedEdits && unsavedEdits.id ? unsavedEdits.id : poi.id;
        const result = await dispatch(VmsState.MultiThunks.updateEntities(poi.venueId, [poi.uuid], unsavedEdits, false, null, entities));

        if (result && isV5) {
            dispatch(LocusLabsMap.actions.deselectAllFeatures());
            dispatch(LandscapeState.actionCreators.removeEntity(id.replace(/#/g, '')));
        }
    };

    onMovePoi = async () => {
        const { dispatch, poi, movePoiMode } = this.props;
        if (movePoiMode) return;
        dispatch(VmsState.MultiThunks.onMovePoi(poi));
    };

    onModalClose = userResponse => {
        const { dispatch } = this.props;
        dispatch(VmsState.ViewReducer.Actions.hidePoiConfirmationModal());

        if (userResponse) this.onRevert();
    };

    openDirtyEntityModal = () => {
        const { dispatch, unsavedEdits, movePoiMode } = this.props;

        const { venueId, query } = this.getLocationInfo();

        R.isEmpty(unsavedEdits) && R.isEmpty(movePoiMode) ? dispatch(Routing.Actions.goToVenue(venueId, query)) : dispatch(VmsState.ViewReducer.Actions.showPoiConfirmationModal());
    };

    openRemoveModal = () => {
        const { dispatch, location } = this.props;
        const venueId = Routing.Selectors.locLocationVenueId(location);
        dispatch(VmsState.ViewReducer.Actions.showRemoveConfirmationModal(venueId));
    };

    onRemoveModalClose = async userResponse => {
        const { dispatch, location, poi } = this.props;

        const venueId = Routing.Selectors.locLocationVenueId(location);
        (await userResponse) ? this.onRemove() : this.onRevert();
        dispatch(VmsState.ViewReducer.Actions.hideRemoveConfirmationModal(venueId, userResponse, poi.id));
    };

    onSelectTab = tab => {
        const { dispatch, location } = this.props;

        dispatch(Routing.Actions.changeTab(location, tab));
    };

    onOpenPolygon = (polygonFeatureId, rawFeatureId) => {
        const { dispatch, location } = this.props;

        // TODO : use rawFeatureId to get the polygonFeature once features and entities are separate
        dispatch(VmsState.MultiThunks.openLocation(polygonFeatureId, location));
    };

    renderPoiHistory = () => {
        const { polygonId, rawFeatureId } = this.props;
        // const pastEntities = [poi]
        // TODO when we have history: pass in all 'pastEntities'
        return <PoiHistorySheet onClose={() => this.onClose(false)} onOpenPolygon={this.onOpenPolygon} polygonId={polygonId} rawFeatureId={rawFeatureId} />;
    };

    openImageInNewTab = async image => {
        const { dispatch } = this.props;
        const url = await dispatch(VmsState.PoiStatusReducers.Thunks.getBlob(image));
        return url;
    };

    // =================================================================================================================
    // From PoiSheet
    // =================================================================================================================

    onUpdatePoiField = (key, value) => {
        const sideEffectUpdate = key === 'terminal' ? { floor: null } : {};
        this.onEdit({ [key]: value, ...sideEffectUpdate });
    };

    setStatus = openOrClosed => () => {
        const { poi } = this.props;
        const currentId = poi.id;
        if (openOrClosed === 'open') {
            // this.onUpdatePoiFields({ id: convertToOpen(currentId) })
            console.log('Cannot currently open a closed POI');
        } else {
            this.onEdit({ id: convertToClosed(currentId) }); // category: 'unoccupied'
            this.openRemoveModal();
        }
    };

    selectEditor = tab => {
        switch (tab) {
            case 'display':
                return DisplayEditor;
            case 'info':
                return InfoEditor;
            case 'images':
                return ImageEditor;
            case 'tags':
                return TagEditor;
            case 'internal':
                return InternalEditor;
            case 'json':
                return JsonEditor;
            default:
                return InfoEditor;
        }
    };

    createOpenClosedOption = (openOrClosed, setStatus) =>
        openOrClosed === 'closed' ? { label: 'Reopen POI', onSelect: setStatus('open'), icon: 'icon-poi-restore' } : { label: 'Remove POI', onSelect: setStatus('closed'), icon: 'icon-trash' };

    createIconMenuOptions = (isV5, openOrClosed, onDuplicatePoi) => {
        const openCloseOption = this.createOpenClosedOption(openOrClosed, this.setStatus);
        return isV5 ? [openCloseOption] : [openCloseOption, { label: 'Duplicate POI', onSelect: onDuplicatePoi, icon: 'icon-duplicate-POI' }];
    };

    // =================================================================================================================

    renderPoiSheet = () => {
        const {
            accountId,
            poi,
            unsavedEdits,
            terminals,
            terminalsToLevels,
            isDisabled,
            allTags,
            categories,
            isSaving,
            isV5,
            showRemove,
            errorMessage,
            selectedTabId,
            modalOpen,
            category,
            v4Icon,
            iconOptions,
            userData,
            openOrClosed,
            changedFields,
            saveIsVisible,
            isNameEmpty,
            isMovablePointPoi,
            movePoiMode,
            isTranslatedVenue,
        } = this.props;

        const poiInProgress = { ...poi, ...unsavedEdits };

        if (!poiInProgress) {
            return null;
        }

        const Editor = this.selectEditor(selectedTabId);
        const iconMenuOptions = this.createIconMenuOptions(isV5, openOrClosed, this.onDuplicatePoi);

        return (
            <div style={{ height: '100%' }}>
                <div className={classNames(s.poiSheet)}>
                    <div className={classNames(s.overlay, { [s.active]: isSaving })}>
                        <Spinner loading={isSaving} />
                    </div>
                    <PoiHeader
                        poi={poiInProgress}
                        iconMenuOptions={iconMenuOptions}
                        isV5={isV5}
                        isDisabled={isDisabled}
                        isNew={this.isNewPoi()}
                        onClose={this.openDirtyEntityModal}
                        isPointPoi={isMovablePointPoi}
                        onMovePoi={this.onMovePoi}
                    />
                    <PoiName
                        isDisabled={isDisabled}
                        isNameEmpty={isNameEmpty}
                        onDuplicatePoi={this.onDuplicatePoi}
                        onUpdatePoiField={this.onUpdatePoiField}
                        openOrClosed={openOrClosed}
                        poi={poiInProgress}
                        setStatus={this.setStatus}
                    />

                    <PoiTabBar changedFields={changedFields} onSelectTab={this.onSelectTab} selectedTabId={selectedTabId} />

                    <div className={s.editorWrapper}>
                        <Editor
                            accountId={accountId}
                            allTags={allTags}
                            categories={categories}
                            category={category}
                            changedFields={changedFields}
                            disabled={isDisabled}
                            displayTags={poiInProgress.displayTags ? R.uniq(poiInProgress.displayTags) : []}
                            iconOptions={iconOptions}
                            images={poiInProgress.images}
                            isV5={isV5}
                            logo={R.isNil(poiInProgress.logo) ? [] : [poiInProgress.logo]}
                            onUpdatePoiField={this.onUpdatePoiField}
                            onUpdatePoiFields={this.onEdit}
                            openImageInNewTab={this.openImageInNewTab}
                            poi={poiInProgress}
                            tags={poiInProgress.tags ? R.uniq(poiInProgress.tags) : []}
                            terminals={terminals}
                            terminalsToLevels={terminalsToLevels}
                            v4Icon={v4Icon}
                        />
                    </div>

                    <PoiSave
                        changedFields={changedFields}
                        errorMessage={errorMessage}
                        isSaving={isSaving}
                        isVisible={saveIsVisible}
                        onClose={this.openDirtyEntityModal}
                        onSave={this.onSavePoi}
                        poi={poiInProgress}
                        isMovePoiSaveMode={movePoiMode === 'save'}
                        isTranslatedVenue={isTranslatedVenue}
                    />
                </div>

                <ConfirmationModal open={modalOpen} onClose={this.onModalClose} />
                <ConfirmationModal okLabel="REMOVE POI" onClose={this.onRemoveModalClose} open={showRemove} subtitle="Are you sure you want to remove this POI?" title="Remove this POI from the map" />
            </div>
        );
    };

    render = () => {
        const { showHistory } = this.props;

        return showHistory ? this.renderPoiHistory() : this.renderPoiSheet();
    };
}

const mapStateToProps = state => {
    const userData = VmsState.SessionReducer.Selectors.getUserData(state);
    const accountId = VmsState.SessionReducer.Selectors.getAccountId(state);
    const isInternal = R.prop('custom:Internal', userData);
    const location = Routing.Selectors.getLocation(state);
    const view = Routing.Selectors.getLocationView(state);
    const poiId = Routing.Selectors.getLocationPoiId(state);
    const venueId = Routing.Selectors.getLocationVenueId(state);
    const venueDetails = LandscapeState.selectors.getVenueSummary(venueId);
    const isTranslatedVenue = venueDetails?.locale && venueDetails.locale !== 'en';
    const selectedTabId = Routing.Selectors.getLocationSelectedTabId(state);
    const poiStatus = VmsState.PoiStatusReducers.Selectors.getPoiStatus(state)[poiId];
    const unsavedEdits = VmsState.PoiStatusReducers.Selectors.getPoiEdits(state)[poiId] || {};
    const isSaving = poiStatus === POI_STATUS_TYPE.SAVING;
    const errorMessage = poiStatus === POI_STATUS_TYPE.ERROR ? VmsState.PoiStatusReducers.Selectors.getPoiErrors(state)[poiId] : '';
    const categories = LandscapeState.selectors.categoriesForVenue(venueId, isInternal, state);
    const terminalsToLevels = LandscapeState.selectors.terminalsToLevels(venueId);
    const terminals = R.keys(terminalsToLevels);
    const allTags = LandscapeState.selectors.buildTagsArray(venueId);
    const isV5 = LandscapeState.selectors.isVenueV5(venueId, state);
    const showPoiConfirmationModal = VmsState.ViewReducer.Selectors.getShowPoiConfirmationModal(state);
    const showRemove = VmsState.ViewReducer.Selectors.getShowRemoveConfirmationModal(state);
    const entity = LandscapeState.selectors.getPoi(venueId, poiId);
    const updatedPoiCoords = VmsState.PoiStatusReducers.Selectors.getUpdatePoiCoords(state);
    const lastSavedPoiCoords = VmsState.PoiStatusReducers.Selectors.getLastSavedPoiCoords(state);

    // If there are edits to the entity category/icon use that otherwise use the entity category/icon
    const entityCategoryName = getEntityCategory(unsavedEdits, entity);
    const v4Icon = getEntityIcon(unsavedEdits, entity);
    const category = LandscapeState.selectors.getCategory(venueId, entityCategoryName, state) || {};
    const iconOptions = !R.isEmpty(category) && isV5 !== undefined ? LandscapeState.selectors.getBadgeOptions(category, isV5, venueId, accountId, state) : [];
    const polygonId = entity && entity.id ? LandscapeState.selectors.polygonIdsForEntity(entity.id.replace(/#/g, ''))[0] : '';
    const rawFeatureId = VmsState.ViewReducer.Selectors.getRawFeatureId(state);
    const polygonFeature = polygonId ? LandscapeState.selectors.getFeature(polygonId) : null;
    const isMovablePointPoi = entity && entity.id && LandscapeState.selectors.isEntityAMovablePoint(entity.id);
    const entityPosition = entity && entity.id && LandscapeState.selectors.positionForEntity(entity.id.replace(/#/, ''), state);
    const movePoiMode = VmsState.ModeReducer.Selectors.getMode(state).movePoiMode;

    const cantEditPortal = !isInternal && !!entityCategoryName && entityCategoryName.includes('portal');
    const hasObjectStoreWritePermission = VmsState.SessionReducer.Selectors.hasWritePermissionToResource(venueId, LliamUtilities.CONSTANTS.OBJECTSTORES, state);

    const isDisabled = (entity && entity.id && entity.id.includes('#')) || cantEditPortal || !hasObjectStoreWritePermission;

    const openOrClosed = entity && entity.id && entity.id.includes('#') ? 'closed' : 'open';
    const changedFields = R.keys(unsavedEdits);

    const changesLength = entity
        ? R.without(['imagesHasFailedImages', 'logoHasFailedImages'], changedFields).length + (entity.imagesHasFailedImages === true ? 1 : 0) + (entity.logoHasFailedImages === true ? 1 : 0)
        : 0;

    const name = R.propOr('', 'name', { ...entity, ...unsavedEdits });
    const isNameEmpty = name.replace(/\s+/g, '') === '';
    const saveIsVisible = openOrClosed === 'open' && changesLength > 0 && !isNameEmpty;

    const errorMessageCheckFailedImages =
        entity && (entity.imagesHasFailedImages === true || entity.logoHasFailedImages === true) ? 'Please delete all failed image files to save your changes' : errorMessage;

    const errorMessageEntityEditValidation = validation.findFatalEntityEditValidationErrors(unsavedEdits) || errorMessage;

    // Get all the categories if you're an internal user or the PoiSheet is disabled.
    // This allows the portal icon to appear in the Display tab when disabled for external users.
    // External users cannot select the portal category on the dropdown since it affects navigation.
    const updatedCategories = isInternal || isDisabled ? categories : R.reject(c => c.canonicalName.includes('portal'), categories);

    const showHistory = view === 'map' && polygonFeature && polygonFeature.properties && polygonFeature.properties.category === 'unoccupied' && isV5;

    const openLocation = VmsState.PoiStatusReducers.Selectors.getOpenLocation(state);
    const feature = VmsState.PoiStatusReducers.Selectors.getFeature(state);

    return {
        accountId,
        allTags,
        categories: updatedCategories,
        category,
        changedFields,
        errorMessage: errorMessageCheckFailedImages || errorMessageEntityEditValidation,
        feature,
        iconOptions,
        isDisabled,
        isNameEmpty,
        isSaving,
        isV5,
        location,
        modalOpen: showPoiConfirmationModal,
        openLocation,
        openOrClosed,
        poi: entity,
        polygonId,
        rawFeatureId,
        isMovablePointPoi,
        entityPosition,
        saveIsVisible,
        selectedTabId,
        showHistory,
        showRemove,
        terminals,
        terminalsToLevels,
        unsavedEdits,
        userData,
        v4Icon,
        movePoiMode,
        updatedPoiCoords,
        lastSavedPoiCoords,
        isTranslatedVenue
    };
};

const getEntityCategory = (unsavedEdits, entity) => {
    let name = '';
    if (!R.isEmpty(unsavedEdits) && unsavedEdits.category) name = VmsState.selectors.getEditedCategory(unsavedEdits);
    else if (entity !== null) name = Entity.getEntityCategory(entity);
    return name;
};

const getEntityIcon = (unsavedEdits, entity) => {
    let icon = '';
    if (!R.isEmpty(unsavedEdits) && unsavedEdits.icon) icon = VmsState.selectors.getEditedIcon(unsavedEdits);
    else if (entity !== null) icon = Entity.getIcon(entity);
    return icon;
};

export default connect(mapStateToProps)(PoiSheetContainer);
